import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../utils";
import { RootState } from "../../redux/store";
import {
  ConnectDevicesToProviderPayload,
  ConnectVehiclesToProviderPayload,
  CreateAnalyticVehiclesPayload,
  VehicleApi,
} from "./telemetryApi";
import { DisconnectVehicleFromProviderPayload } from "@qivia/schemas-telemetry";

export interface VehiclesOfACompanyState {
  analyticVehiclesListOfACompanyStatus: QueryStatus;
  analyticVehiclesListOfACompany: VehicleApi[];
  createAnalyticVehiclesStatus: QueryStatus;
  connectVehiclesToProviderStatus: QueryStatus;
  connectDevicesToProviderStatus: QueryStatus;
  disconnectVehicleFromProviderStatus: QueryStatus;
}

const initialState: VehiclesOfACompanyState = {
  analyticVehiclesListOfACompanyStatus: "idle",
  analyticVehiclesListOfACompany: [],
  createAnalyticVehiclesStatus: "idle",
  connectVehiclesToProviderStatus: "idle",
  connectDevicesToProviderStatus: "idle",
  disconnectVehicleFromProviderStatus: "idle",
};

export const analyticVehiclesListOfACompanyAsync = createAsyncThunk(
  "telemetry/analyticVehiclesListOfACompany/call",
  async (payload: { companyUuid: string }) => {
    const axios = authAxios();
    const response = await axios.get<VehicleApi[]>(
      `/telemetry/vehicles/company/${payload.companyUuid}`,
    );
    return response.data.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  },
);

export const createAnalyticVehiclesToCheckTheirEgibilitiesAsync =
  createAsyncThunk(
    "telemetry/createAnalyticVehiclesToCheckTheirEgibilities/call",
    async (payload: CreateAnalyticVehiclesPayload) => {
      const axios = authAxios();
      await axios.post(`/telemetry/vehicles/create_analytic_vehicles`, payload);
      return;
    },
  );

export const connectVehiclesToProviderAsync = createAsyncThunk(
  "telemetry/connectVehiclesToProvider/call",
  async (
    payload: ConnectVehiclesToProviderPayload & { companyUuid: string },
    { rejectWithValue, dispatch },
  ) => {
    const axios = authAxios();
    await axios.post(`/telemetry/vehicles/connect_vehicles_to_provider`, {
      vehicleUuids: payload.vehicleUuids,
      connectionPlan: payload.connectionPlan,
    });

    const maxTime = 15000;
    const interval = 1000;
    const startTime = Date.now();

    const poll = async (): Promise<void> => {
      try {
        const vehiclesFetched = await dispatch(
          analyticVehiclesListOfACompanyAsync({ companyUuid: payload.companyUuid }),
        ).unwrap();

        const vehiclesToCheck = vehiclesFetched.filter((v) =>
          payload.vehicleUuids.includes(v.uuid),
        );
        const allPending = vehiclesToCheck.every(
          (v) => v.connectionStatus === "PENDING",
        );

        if (allPending) return;

        if (Date.now() - startTime > maxTime) {
          throw new Error("Timeout reached");
        }

        await new Promise((resolve) => setTimeout(resolve, interval));
        return poll();
      } catch (err: unknown) {
        rejectWithValue(err as string);
      }
    };

    await poll();
  },
);

export const connectDevicesToProviderAsync = createAsyncThunk(
  "telemetry/connectDevicesToProvider/call",
  async (
    payload: ConnectDevicesToProviderPayload & { companyUuid: string },
    { dispatch },
  ) => {
    const axios = authAxios();
    await axios.post(`/telemetry/vehicles/connect_devices_to_provider`, {
      data: payload.data,
    });

    const vehicleUuids = payload.data.map((d) => d.vehicleUuid);
    const maxTime = 15000;
    const interval = 1000;
    const startTime = Date.now();

    const poll = async (): Promise<void> => {
      const vehiclesFetched = await dispatch(
        analyticVehiclesListOfACompanyAsync({ companyUuid: payload.companyUuid }),
      ).unwrap();

      const vehiclesToCheck = vehiclesFetched.filter((v) =>
        vehicleUuids.includes(v.uuid),
      );
      const allApproved = vehiclesToCheck.every(
        (v) =>
          v.connectionStatus === "APPROVED" ||
          v.connectionStatus === "CONNECTED",
      );

      if (allApproved) return;

      if (Date.now() - startTime > maxTime) {
        throw new Error("Timeout reached");
      }

      await new Promise((resolve) => setTimeout(resolve, interval));
      return poll();
    };

    await poll();
  },
);

export const disconnectVehicleFromProviderAsync = createAsyncThunk(
  "telemetry/disconnectVehicleFromProvider/call",
  async (payload: DisconnectVehicleFromProviderPayload) => {
    const axios = authAxios();
    await axios.post(
      `/telemetry/vehicles/disconnect_vehicle_from_provider`,
      payload,
    );
    return;
  },
);

export const telemetrySlice = createSlice({
  name: "telemetry",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetConnectVehiclesToProviderStatus: (state) => {
      state.connectVehiclesToProviderStatus = "idle";
    },
    resetConnectDevicesToProviderStatus: (state) => {
      state.connectDevicesToProviderStatus = "idle";
      state.analyticVehiclesListOfACompanyStatus = "idle";
    },
    resetDisconnectVehicleFromProviderStatus: (state) => {
      state.disconnectVehicleFromProviderStatus = "idle";
    },
    refreshAnalyticVehiclesListOfACompany: (state) => {
      state.analyticVehiclesListOfACompanyStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(analyticVehiclesListOfACompanyAsync.pending, (state) => {
        state.analyticVehiclesListOfACompanyStatus = "processing";
      })
      .addCase(
        analyticVehiclesListOfACompanyAsync.fulfilled,
        (state, action) => {
          state.analyticVehiclesListOfACompany = action.payload;
          state.analyticVehiclesListOfACompanyStatus = "success";
        },
      )
      .addCase(analyticVehiclesListOfACompanyAsync.rejected, (state) => {
        state.analyticVehiclesListOfACompanyStatus = "failed";
      })
      .addCase(
        createAnalyticVehiclesToCheckTheirEgibilitiesAsync.pending,
        (state) => {
          state.createAnalyticVehiclesStatus = "processing";
        },
      )
      .addCase(
        createAnalyticVehiclesToCheckTheirEgibilitiesAsync.fulfilled,
        (state) => {
          state.createAnalyticVehiclesStatus = "success";
        },
      )
      .addCase(
        createAnalyticVehiclesToCheckTheirEgibilitiesAsync.rejected,
        (state) => {
          state.createAnalyticVehiclesStatus = "failed";
        },
      )
      .addCase(connectVehiclesToProviderAsync.pending, (state) => {
        state.connectVehiclesToProviderStatus = "processing";
      })
      .addCase(connectVehiclesToProviderAsync.fulfilled, (state) => {
        state.connectVehiclesToProviderStatus = "success";
      })
      .addCase(connectVehiclesToProviderAsync.rejected, (state) => {
        state.connectVehiclesToProviderStatus = "failed";
      })
      .addCase(connectDevicesToProviderAsync.pending, (state) => {
        state.connectDevicesToProviderStatus = "processing";
      })
      .addCase(connectDevicesToProviderAsync.fulfilled, (state) => {
        state.connectDevicesToProviderStatus = "success";
      })
      .addCase(connectDevicesToProviderAsync.rejected, (state) => {
        state.connectDevicesToProviderStatus = "failed";
      })
      .addCase(disconnectVehicleFromProviderAsync.pending, (state) => {
        state.disconnectVehicleFromProviderStatus = "processing";
      })
      .addCase(disconnectVehicleFromProviderAsync.fulfilled, (state) => {
        state.disconnectVehicleFromProviderStatus = "success";
      })
      .addCase(disconnectVehicleFromProviderAsync.rejected, (state) => {
        state.disconnectVehicleFromProviderStatus = "failed";
      });
  },
});

export const selectAnalyticVehiclesListOfACompany = (state: RootState) =>
  state.telemetry.analyticVehiclesListOfACompany;
export const selectAnalyticVehiclesListOfACompanyStatus = (state: RootState) =>
  state.telemetry.analyticVehiclesListOfACompanyStatus;
export const selectCreateAnalyticVehiclesStatus = (state: RootState) =>
  state.telemetry.createAnalyticVehiclesStatus;
export const selectConnectVehiclesToProviderStatus = (state: RootState) =>
  state.telemetry.connectVehiclesToProviderStatus;
export const selectConnectDevicesToProviderStatus = (state: RootState) =>
  state.telemetry.connectDevicesToProviderStatus;
export const selectDisconnectVehicleFromProviderStatus = (state: RootState) =>
  state.telemetry.disconnectVehicleFromProviderStatus;

export default telemetrySlice.reducer;
